import {useCallback, useEffect, useRef} from 'react';

import type {SSOdMessage} from '@pexip/infinity';
import {isSSOdMessage} from '@pexip/infinity';

/**
 * Manages the SSO popup lifecycle: opening, polling for close,
 * listening for `postMessage`, and cleanup.
 *
 * @example
 * ```ts
 * const {openPopup, closePopup, popupRef} = useSsoPopup({
 *     onSsoMessage: (message) => registration.handleSsoResult(message),
 *     onPopupClosed: () => registration.setStep('register'),
 * });
 * ```
 */
export const useSsoPopup = ({
    onSsoMessage,
    onPopupClosed,
    onPopupBlocked,
}: {
    onSsoMessage: (message: SSOdMessage) => void;
    onPopupClosed: () => void;
    onPopupBlocked?: () => void;
}) => {
    const popupRef = useRef<Window | null>(null);
    const pollingIdRef = useRef<number>(undefined);

    const cleanup = useCallback(() => {
        if (pollingIdRef.current) {
            clearInterval(pollingIdRef.current);
            pollingIdRef.current = undefined;
        }
    }, []);

    const handleMessage = useCallback(
        (event: MessageEvent) => {
            if (event.origin !== window.location.origin) {
                return;
            }
            if (!isSSOdMessage(event.data)) {
                return;
            }

            cleanup();
            popupRef.current?.close();
            window.removeEventListener('message', handleMessage);

            onSsoMessage(event.data);
        },
        [onSsoMessage, cleanup],
    );

    const closePopup = useCallback(() => {
        popupRef.current?.close();
        cleanup();
        window.removeEventListener('message', handleMessage);
    }, [cleanup, handleMessage]);

    const openPopup = useCallback(
        (redirectUrl: string, disablePopupFlow?: boolean) => {
            popupRef.current?.close();

            if (redirectUrl && disablePopupFlow) {
                window.location.href = redirectUrl;
                return;
            }

            popupRef.current = window.open('', '', 'width=800,height=600');

            if (!popupRef.current || popupRef.current.closed) {
                onPopupBlocked?.();
                return;
            }

            if (redirectUrl) {
                popupRef.current.location.href = redirectUrl;
            }

            if (!pollingIdRef.current) {
                pollingIdRef.current = window.setInterval(() => {
                    if (popupRef.current?.closed) {
                        cleanup();
                        window.removeEventListener('message', handleMessage);
                        onPopupClosed();
                    }
                }, 1000);

                window.addEventListener('message', handleMessage);
            }
        },
        [handleMessage, onPopupClosed, onPopupBlocked, cleanup],
    );

    useEffect(
        () => () => {
            cleanup();
            window.removeEventListener('message', handleMessage);
        },
        [cleanup, handleMessage],
    );

    return {openPopup, closePopup, popupRef};
};
